import {
  Dvr,
  Edit,
  Info,
  ReceiptLongOutlined,
  Description,
  Hardware,
  AddAlert,
  ArrowCircleUp,
  FlashOn,
  Upload,
  Update,
  Restore,
  RestartAlt,
  DeveloperBoard,
  SystemUpdate,
} from "@mui/icons-material";
import GppBadIcon from "@mui/icons-material/GppBad";
import GppGoodIcon from "@mui/icons-material/GppGood";
import { Box } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AnyAction, Dispatch } from "redux";
import { DataLoadingText } from "../../components/atoms/text/dataLoadingText";
import { SubpageTitle, TextTitle } from "../../components/atoms/text/titles";
import Page from "../../components/molecules/page";
import { AppDispatch, StoreState } from "../../state/configureStore";
import { updateReducerWithThunk } from "../../state/reducerUtils";
import {
  deactivateMachine,
  pushLatestRecipes,
  setMachineAsActive,
} from "../../state/thunks/machines";
import { fetchTargetSoftware } from "../../state/thunks/targetSoftwareThunks";
import { clearDeviceIdOverride } from "../../state/reducers/slices/deviceIdOverrideSlice";
import { padding, text_colors } from "../../theme";
import { FetchFromBackoffice } from "../../utils/request";
import { SoldRecipes } from "../customers/customer";
import TestSequence from "./TestSequence";
import { AddMachineDescription } from "./addMachineDescription";
import { Comments } from "./comments/comments";
import EditMachineModal from "./editMachineModal";
import { HubHealth } from "./hubHealth";
import { LogsSection } from "./logs/logSection";
import { MachineDetails, MachineDetailsView } from "./machineDetails";
import { MachineTags } from "./machineTags";
import { RecipesList } from "./recipes/recipes-list";
import UptimeTable from "./uptimeTable";
import VerificationModal from "./verificationModal";
import { HubRecipes } from "./hubRecipes";
import { AssetHistory } from "./assetHistory/assetHistory";
import VerifyFlashModal from "./verifyFlashModal";
import PushFirmwareModal from "./pushFirmwareModal";
import VerifyFactoryResetModal from "./verifyFactoryResetModal";
import VerifyChangeMotherBoardModal from "./verifyChangeMotherBoardModal";
import UpdateTargetSoftwareModal from "./updateTargetSoftwareModal";
import StaticMap from "../../components/molecules/maps/static/staticMap";
import { MachineStyle } from "./styles/machineStyle";
import { MachineAlerts } from "./alerts/machineAlerts";
import VerifyRestartModal from "./verifyRestartModal";

const infoRowWidth = "100%";

export const Machine = (
  state: ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>,
) => {
  const { serialId } = useParams<{ serialId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const targetedSwVersion = useSelector((s: StoreState) =>
    serialId ? s.targetSoftware.bySerialId[serialId]?.value : undefined,
  );
  const deviceIdOverride = useSelector((s: StoreState) =>
    serialId ? s.deviceIdOverride.bySerialId[serialId] : undefined,
  );

  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [descriptionModalOpen, setDescriptionModalOpen] =
    useState<boolean>(false);
  const [verifiUpdate, setVerifiUpdate] = useState<boolean>(false);
  const [verifyFlash, setVerifyFlash] = useState<boolean>(false);
  const [pushFirmware, setPushFirmware] = useState<boolean>(false);
  const [verifyReset, setVerifyReset] = useState<boolean>(false);
  const [verifyChangeMotherBoard, setVerifyChangeMotherBoard] =
    useState<boolean>(false);
  const [updateTargetSoftwareOpen, setUpdateTargetSoftwareOpen] =
    useState<boolean>(false);
  const [verifyRestart, setVerifyRestart] = useState<boolean>(false);
  const [details, setDetails] = useState<MachineDetails>();

  const isMobile = state.dimensions.device !== "desktop";

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "auto" });
  }, []);

  useEffect(() => {
    if (serialId) {
      dispatch(fetchTargetSoftware(serialId));
      FetchFromBackoffice<MachineDetails>(`machines/${serialId}`).then(
        (data) => {
          setDetails(data);
        },
      );
    }
  }, [serialId, dispatch]);

  useEffect(() => {
    if (state.savingState === "ready") {
      setEditModalOpen(false);
      setDescriptionModalOpen(false);
    }
  }, [state.savingState]);

  useEffect(() => {
    if (!serialId || !deviceIdOverride) {
      return;
    }
    const d = details?.deviceId;
    if (
      d !== undefined &&
      d !== deviceIdOverride.deviceIdBeforeChange
    ) {
      dispatch(clearDeviceIdOverride(serialId));
    }
  }, [details?.deviceId, serialId, deviceIdOverride, dispatch]);

  const UptimeSection = useCallback(() => {
    return (
      <Box sx={{ ...MachineStyle.infoSection }}>
        <SubpageTitle title={"Uptime"} />
        <UptimeTable serialId={serialId} />
      </Box>
    );
  }, [serialId]);

  const MachineInfo = useCallback(() => {
    const nodeUrl =
      "/customers/" +
      (details?.parents?.length
        ? details?.parents.map((p) => p.id)?.join("/") + "/" + details?.nodeId
        : "");
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 2, minWidth: 500 }}>
          {(!details || !ownerCompanyName) && state.isLoading_machineInfoBox ? (
            <DataLoadingText showSpinner text={"Loading hub information ..."} />
          ) : (
            <React.Fragment>
              {details?.location ? (
                <Box sx={{ maxWidth: infoRowWidth, paddingTop: "10px" }}>
                  <Box
                    style={{
                      width: "100%",
                      height: 250,
                      display: "flex",
                      alignSelf: "center",
                    }}
                  >
                    <StaticMap locations={[details.location]} />
                  </Box>
                </Box>
              ) : null}

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "row",
                  marginTop: "30px",
                  flexWrap: "wrap",
                  maxWidth: infoRowWidth,
                }}
              >
                <Box sx={{ ...MachineStyle.infoSection }}>
                  <TextTitle title={"Overview"} />
                  <MachineDetailsView
                    nodeUrl={nodeUrl}
                    nodeName={details?.nodeName}
                    ownerCompanyName={ownerCompanyName}
                    ownerCompanyId={ownerCompanyId}
                    machineDetails={
                      details
                        ? {
                            ...details,
                            deviceId: deviceIdOverride?.forceEmpty
                              ? ""
                              : details.deviceId,
                            targetedSwVersion,
                          }
                        : undefined
                    }
                  />
                </Box>

                {!isMobile && (
                  <span
                    style={{
                      width: "1px",
                      backgroundColor: text_colors.grey,
                      marginRight: "30px",
                    }}
                  ></span>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    gap: "10px",
                    justifyContent: isMobile ? "space-between" : "center",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  <HubHealth machineDetails={details} />
                  <HubRecipes serialId={serialId} />
                </Box>

                {/* <MachineErrorList SerialId={machine?.serialId ?? ""} /> */}
              </Box>
            </React.Fragment>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Comments serialId={serialId} />
        </Box>
      </Box>
    );
  }, [
    state.isLoading_machineInfoBox,
    details,
    state.dimensions.device,
    targetedSwVersion,
    deviceIdOverride,
  ]);

  const ownerCompanyName = details?.parents?.length
    ? details?.parents[0].name
    : undefined;
  const ownerCompanyId = details?.parents?.length
    ? details?.parents[0].id
    : undefined;

  const canRunTestSequence = !ownerCompanyId && !deviceIdOverride?.forceEmpty && details?.deviceId ? true : false

  if (!details) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <DataLoadingText showSpinner text={"Loading hub information ..."} />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <div ref={topRef} />
      {details && editModalOpen ? (
        <EditMachineModal
          machine={details}
          open={editModalOpen}
          closeModal={() => setEditModalOpen(false)}
        />
      ) : null}
      {details && descriptionModalOpen ? (
        <AddMachineDescription
          serialId={details?.serialNumber}
          description={details?.description}
          open={descriptionModalOpen}
          closeModal={() => setDescriptionModalOpen(false)}
          onSaved={(newText) =>
            setDetails((curr) => ({ ...curr!, description: newText ?? "" }))
          }
        />
      ) : null}
      {details && verifiUpdate ? (
        <VerificationModal
          details={details}
          open={verifiUpdate}
          closeModal={() => setVerifiUpdate(false)}
        />
      ) : null}
      {details && verifyFlash ? (
        <VerifyFlashModal
          details={details}
          open={verifyFlash}
          closeModal={() => setVerifyFlash(false)}
        />
      ) : null}
      {details && pushFirmware ? (
        <PushFirmwareModal
          details={details}
          open={pushFirmware}
          closeModal={() => setPushFirmware(false)}
        />
      ) : null}
      {details && verifyReset ? (
        <VerifyFactoryResetModal
          details={details}
          open={verifyReset}
          closeModal={() => setVerifyReset(false)}
        />
      ) : null}
      {details && verifyChangeMotherBoard ? (
        <VerifyChangeMotherBoardModal
          details={details}
          open={verifyChangeMotherBoard}
          closeModal={() => setVerifyChangeMotherBoard(false)}
        />
      ) : null}
      {details && updateTargetSoftwareOpen ? (
        <UpdateTargetSoftwareModal
          details={details}
          open={updateTargetSoftwareOpen}
          closeModal={() => setUpdateTargetSoftwareOpen(false)}
        />
      ) : null}
       {details && verifyRestart ? (
        <VerifyRestartModal
          details={details}
          open={verifyRestart}
          closeModal={() => setVerifyRestart(false)}
        />
      ) : null}
      <Page
        sideScroll
        title={details?.machineName || serialId || "Hub"}
        description={details?.description}
        Menu={{
          Id: "machine-page-menu",
          ButtonTitle: "Actions",
          MenuItems: [
            {
              Label: "Edit hub name",
              Icon: <Edit />,
              onClick: () => setEditModalOpen(true),
            },
            {
              Label: details?.description
                ? "Edit hub description"
                : "Add hub description",
              Icon: details?.description ? <Edit /> : <Description />,
              onClick: () => setDescriptionModalOpen(true),
            },
            {
              toolTip: details?.nodeName
                ? undefined
                : "Hub need to be connected to store to be activated",
              Enabled: details?.nodeName !== undefined,
              Label: details?.monitored ? "Deactivate hub" : "Activate hub",
              Icon: details?.monitored ? (
                <GppBadIcon
                  sx={{ color: text_colors.greyDark, marginBottom: "-5px" }}
                />
              ) : (
                <div>
                  <GppGoodIcon
                    sx={{ color: text_colors.greyBlue, marginBottom: "-5px" }}
                  />
                </div>
              ),
              onClick: () => {
                if (serialId) {
                  if (details?.monitored) {
                    dispatch(
                      updateReducerWithThunk({
                        payload: {
                          serialNumber: serialId,
                        },
                        type: "MACHINE_DEACTIVATED",
                      }),
                    );
                    dispatch(
                      deactivateMachine({
                        serialNumber: serialId,
                      }),
                    );
                  } else {
                    dispatch(
                      updateReducerWithThunk({
                        payload: {
                          serialNumber: serialId,
                        },
                        type: "MACHINE_ACTIVATED",
                      }),
                    );
                    dispatch(setMachineAsActive({ serialNumber: serialId }));
                  }
                }
              },
            },
            {
              Label: "Update hub",
              Icon: <Update />,
              onClick: () => setVerifiUpdate(true),
            },
            {
              Label: "Push latest recipes",
              toolTip: "",
              Icon: <ArrowCircleUp />,
              onClick: () => {
                if (serialId)
                  dispatch(pushLatestRecipes({ serialNumber: serialId }));
              },
            },
            {
              Label: "Flash firmware",
              toolTip: "",
              Icon: <FlashOn />,
              onClick: () => setVerifyFlash(true),
            },
            {
              Label: "Push latest firmware",
              toolTip: "Push the latest firmware from the database",
              Icon: <Upload />,
              onClick: () => setPushFirmware(true),
            },
            {
              Label: "Factory reset",
              toolTip: "Reset the hub to factory settings",
              Icon: <Restore />,
              onClick: () => setVerifyReset(true),
            },
            {
              Label: "Change Mother Board",
              toolTip: details?.deviceId?.trim()
                ? "Change the mother board of the hub"
                : "A SW Serial Id (device id) is required to change the mother board",
              Icon: <DeveloperBoard />,
              Enabled: Boolean(details?.deviceId?.trim()),
              onClick: () => setVerifyChangeMotherBoard(true),
            },
            {
              Label: "Update Target Software",
              toolTip: "Change the software version that can be expected on this machine after a software update.",
              Icon: <SystemUpdate />,
              onClick: () => setUpdateTargetSoftwareOpen(true),
            },
            {
              Label: "Restart hub",
              toolTip: "Restart the hub",
              Icon: <RestartAlt />,
              onClick: () => setVerifyRestart(true),
            },
          ],
        }}
        tags={<MachineTags tags={details?.tags} serialId={serialId} />}
        tabs={[
          {
            name: "Overview",
            icon: <Info />,
            pageSections: [
              <MachineInfo />,
              <SoldRecipes
                id={serialId}
                queryString={"recipes-run-daily-machine"}
              />,
              <UptimeSection />,
            ],
          },
          {
            name: "Sales",
            icon: <ReceiptLongOutlined />,
            pageSections: [<RecipesList MachineId={serialId} />],
          },
          {
            name: "Hub Logs",
            icon: <Dvr />,
            pageSections: serialId
              ? [<LogsSection serialId={serialId} />]
              : [<></>],
          },
          {
            name: "Hardware",
            icon: <Hardware />,
            pageSections: [<AssetHistory serialId={serialId} />],
          },
          {
            name: "Alerts",
            icon: <AddAlert />,
            pageSections: [<MachineAlerts serialId={serialId!} />],
          },
          {
            name: "Test Sequence",
            icon: <Dvr />,
            pageSections: [
              <TestSequence SwSerialId={details.deviceId} canRunTestSequence={canRunTestSequence} />,
            ],
          }
        ]}
      />
    </React.Fragment>
  );
};

const mapStateToProps = (state: StoreState) => ({
  machines: state.machines,
  isLoading_machineInfoBox: state.machine_fetching === "loading",
  savingState: state.machine_saving,
  customers: state.customers,
  dimensions: state.dimensions,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Machine);
